/**
 * See hdm-order-status.json for API details.
 * integration/specs/custom/hdm-order-status.json
 */
import { getServerCacheScope } from '@/data/cache/getServerCacheScope';
import { ORDER_STATUS_HISTORY_KEY } from '@/data/constants/hdmDataKey';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getSettings, useSettings } from '@/data/Settings';
import { Cache } from '@/data/types/Cache';
import { getUser } from '@/data/User-Server';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getRequestId } from '@/data/utils/getRequestId';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { shrink } from '@/data/utils/keyUtil';
import { laggyMiddleWare } from '@/data/utils/laggyMiddleWare';
import { traceWithId } from '@/data/utils/loggerUtil';
import customOrderHistoryStatus from 'integration/generated/custom/customOrderHistoryStatus';

import { OrderStatusHistoryResponse } from 'integration/generated/custom/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import useSWR, { unstable_serialize } from 'swr';

const getOrderStatusHistorySWRKey = (storeId: string, orderId: string) => ({
	storeId,
	orderId,
});

export const orderStatusHistoryFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		orderId: string,
		query: {
			langId: string;
		},
		params: RequestParams
	): Promise<OrderStatusHistoryResponse> => {
		const res = await customOrderHistoryStatus(pub).orderStatusHistoryFindAll(
			storeId,
			orderId,
			query,
			params
		);
		return res;
	};

/**
 * Server-side function to fetch the order status history.
 * This function is intended to be used in `getServerSideProps` and leverages the server-side cache.
 *
 * @param cache The server-side cache instance.
 * @param context The `GetServerSidePropsContext` object.
 * @returns The order status history or null if an error occurs.
 */
export const getOrderStatusHistory = async (
	cache: Cache,
	context: GetServerSidePropsContext,
	orderId: string,
	params: RequestParams
): Promise<OrderStatusHistoryResponse | null> => {
	const requestId = getRequestId(context);
	traceWithId(requestId, `getOrderStatusHistory: start`);
	const settings = await getSettings(cache, context);
	const user = await getUser(cache, context);
	const { storeId, langId } = getServerSideCommon(settings, context);

	if (!storeId) {
		traceWithId(requestId, `getOrderStatusHistory: end (missing storeId)`);
		return null;
	}
	const props = getOrderStatusHistorySWRKey(storeId, orderId);
	const key = unstable_serialize([shrink(props), ORDER_STATUS_HISTORY_KEY]);
	const cacheScope = getServerCacheScope(context, user.context);

	const value = await cache.get(key);
	if (value) {
		traceWithId(requestId, `getOrderStatusHistory: end (from cache)`);
		return value;
	}

	try {
		const response = await orderStatusHistoryFetcher(false)(
			storeId,
			orderId,
			{
				langId,
			},
			params
		);

		if (response) {
			cache.set(key, response, cacheScope);
			traceWithId(requestId, `getOrderStatusHistory: end (filled cache)`);
		}
		return response;
	} catch (error) {
		console.error(`Failed to fetch order status history:`, error);
		traceWithId(requestId, `getOrderStatusHistory: end (with error)`);
		return null;
	}
};

/**
 * Client-side React hook to fetch specified order status using SWR.
 *
 * @returns An object containing the order history status data, loading state, and error state.
 */
export const useOrderHistoryStatus = (orderId: string) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId, langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const key = getOrderStatusHistorySWRKey(storeId, orderId);
	const {
		data: orderStatusHistory,
		error,
		isLoading,
	} = useSWR<OrderStatusHistoryResponse>(
		storeId ? [shrink(key), ORDER_STATUS_HISTORY_KEY] : null,
		() => orderStatusHistoryFetcher(true)(storeId, orderId, { langId }, params),
		{ use: [laggyMiddleWare] }
	);

	return {
		orderStatusHistory,
		error,
		isLoading,
	};
};
